import { LIBELLES_ADEQUATION, type Adequation, type VerdictCritere } from './adequation.js';
import { AXES, LIBELLES_AXES, type Poids, type Scores } from './bareme.js';
import { LIBELLES_STATUT, type StatutCandidature } from './candidature.js';
import { dateHeureTunis, dateTunis, libelleSource } from './exports.js';

/** Comparateur (docs/UI.md § Écran 5) : deux à quatre candidats côte à côte. */
export const COMPARAISON_MIN = 2;
export const COMPARAISON_MAX = 4;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `?candidatures=id1,id2` : identifiants dédoublonnés, dans l'ordre choisi. */
export function lireSelectionComparaison(valeur: string | null | undefined): { ids: string[] } | { erreur: string } {
  const ids = [
    ...new Set(
      (valeur ?? '')
        .split(',')
        .map((id) => id.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  if (ids.some((id) => !UUID.test(id))) return { erreur: 'Identifiant de candidature invalide' };
  if (ids.length < COMPARAISON_MIN || ids.length > COMPARAISON_MAX) {
    return { erreur: `Choisir de ${COMPARAISON_MIN} à ${COMPARAISON_MAX} candidats à comparer` };
  }
  return { ids };
}

export interface EvaluationComparee {
  evalueeLe: string;
  evalueePar: string;
  baremeVersion: number;
  poids: Poids;
  scores: Scores;
  noteCalculee: number;
  noteFinale: number;
  eliminatoire: boolean;
  /** Texte libre : null pour le rôle lecteur, comme les observations. */
  motifEliminatoire: string | null;
  justificationEcart: string | null;
}

export interface CandidatCompare {
  candidatureId: string;
  personneId: string;
  nom: string | null;
  prenom: string | null;
  anonymisee: boolean;
  /** null pour le rôle lecteur. */
  telephone: string | null;
  email: string | null;
  ville: string | null;
  annonceId: string;
  annonceReference: string;
  annonceIntitule: string;
  famille: string | null;
  source: string;
  statut: StatutCandidature;
  motifSortie: string | null;
  recueLe: string;
  nePlusContacter: boolean;
  profilModifieLe: string | null;
  adequation: Adequation | null;
  criteres: Array<{ libelle: string; type: 'eliminatoire' | 'souhaitable'; verdict: VerdictCritere }>;
  pointsForts: string[];
  vigilance: string[];
  experienceAnnees: number | null;
  potentiel: number | null;
  potentielLe: string | null;
  /** Appels téléphoniques clôturés dont l'issue est connue. */
  appels: number;
  appelsJoints: number;
  dernierContact: string | null;
  rdvLe: string | null;
  /** Note figée sur la candidature ; `evaluation` est la dernière, qui l'a posée. */
  noteEntretien: number | null;
  evaluation: EvaluationComparee | null;
  nbEvaluations: number;
  charteSigneeLe: string | null;
  rapportSigneLe: string | null;
  justificatifs: { attendus: number; recus: number; enRetard: number; echeance: string | null };
}

export interface Comparaison {
  candidats: CandidatCompare[];
  avecCoordonnees: boolean;
}

export type ValeurComparee = string | number | null;

export interface LigneComparaison {
  groupe: string;
  cle: string;
  libelle: string;
  /** `note` : un chiffre après la virgule ; `long` : texte à la ligne. */
  format: 'texte' | 'nombre' | 'note' | 'long';
  /** Sens du meilleur, pour le signaler ; absent : rien à classer. */
  sens?: 'haut' | 'bas';
  valeur: (candidat: CandidatCompare) => ValeurComparee;
}

type SansGroupe = Omit<LigneComparaison, 'groupe'>;

const enGroupe = (groupe: string, lignes: SansGroupe[]): LigneComparaison[] => lignes.map((l) => ({ ...l, groupe }));

const joindre = (textes: readonly string[]): string | null => (textes.length > 0 ? textes.join(' · ') : null);

export const GROUPE_POIDS = 'Poids du barème (%)';

/**
 * Lignes du comparateur, dans l'ordre : l'écran et l'export Excel les lisent
 * ici, pour montrer les mêmes axes avec les mêmes valeurs.
 */
export function lignesComparaison(avecCoordonnees: boolean): LigneComparaison[] {
  const coordonnees: SansGroupe[] = avecCoordonnees
    ? [
        { cle: 'telephone', libelle: 'Téléphone', format: 'texte', valeur: (c) => c.telephone },
        { cle: 'email', libelle: 'E-mail', format: 'texte', valeur: (c) => c.email },
      ]
    : [];

  const candidature = enGroupe('Candidature', [
    { cle: 'annonce', libelle: 'Annonce', format: 'texte', valeur: (c) => `${c.annonceReference} — ${c.annonceIntitule}` },
    { cle: 'famille', libelle: 'Métier', format: 'texte', valeur: (c) => c.famille },
    { cle: 'ville', libelle: 'Ville', format: 'texte', valeur: (c) => c.ville },
    ...coordonnees,
    { cle: 'source', libelle: 'Source', format: 'texte', valeur: (c) => libelleSource(c.source) },
    { cle: 'recue', libelle: 'Reçue le', format: 'texte', valeur: (c) => dateTunis(c.recueLe) },
    {
      cle: 'statut',
      libelle: 'Statut',
      format: 'texte',
      valeur: (c) => (c.motifSortie ? `${LIBELLES_STATUT[c.statut]} (${c.motifSortie})` : LIBELLES_STATUT[c.statut]),
    },
    { cle: 'ne_plus_contacter', libelle: 'Ne plus contacter', format: 'texte', valeur: (c) => (c.nePlusContacter ? 'Oui' : null) },
  ]);

  const tri = enGroupe('Tri automatique', [
    {
      cle: 'adequation',
      libelle: 'Adéquation aux critères',
      format: 'texte',
      valeur: (c) => (c.adequation ? LIBELLES_ADEQUATION[c.adequation] : null),
    },
    {
      cle: 'criteres',
      libelle: 'Critères trouvés dans le CV',
      format: 'texte',
      valeur: (c) =>
        c.criteres.length === 0
          ? null
          : `${c.criteres.filter((k) => k.verdict === 'conforme').length} sur ${c.criteres.length}`,
    },
    {
      cle: 'eliminatoires_manquants',
      libelle: 'Critères éliminatoires manquants',
      format: 'long',
      valeur: (c) => joindre(c.criteres.filter((k) => k.type === 'eliminatoire' && k.verdict === 'manquant').map((k) => k.libelle)),
    },
    {
      cle: 'experience',
      libelle: 'Expérience lue dans le CV (années)',
      format: 'nombre',
      sens: 'haut',
      valeur: (c) => c.experienceAnnees,
    },
    { cle: 'points_forts', libelle: 'Points forts', format: 'long', valeur: (c) => joindre(c.pointsForts) },
    { cle: 'vigilance', libelle: 'Points de vigilance', format: 'long', valeur: (c) => joindre(c.vigilance) },
  ]);

  const telephone = enGroupe('Appels', [
    { cle: 'pastille', libelle: 'Pastille de potentiel', format: 'nombre', sens: 'haut', valeur: (c) => c.potentiel },
    { cle: 'pastille_le', libelle: 'Pastille posée le', format: 'texte', valeur: (c) => dateTunis(c.potentielLe) },
    {
      cle: 'appels',
      libelle: 'Appels aboutis',
      format: 'texte',
      valeur: (c) => (c.appels > 0 ? `${c.appelsJoints} sur ${c.appels}` : null),
    },
    { cle: 'dernier_contact', libelle: 'Dernier contact', format: 'texte', valeur: (c) => dateHeureTunis(c.dernierContact) },
    { cle: 'rdv', libelle: 'Rendez-vous', format: 'texte', valeur: (c) => dateHeureTunis(c.rdvLe) },
  ]);

  const entretien = enGroupe('Entretien', [
    { cle: 'note', libelle: 'Note d’entretien', format: 'note', sens: 'haut', valeur: (c) => c.noteEntretien },
    {
      cle: 'entretien_du',
      libelle: 'Entretien du',
      format: 'texte',
      valeur: (c) => (c.evaluation ? `${dateTunis(c.evaluation.evalueeLe)} par ${c.evaluation.evalueePar}` : null),
    },
    {
      cle: 'note_calculee',
      libelle: 'Note calculée par le barème',
      format: 'note',
      valeur: (c) => c.evaluation?.noteCalculee ?? null,
    },
    {
      cle: 'forcage',
      libelle: 'Note forcée',
      format: 'long',
      valeur: (c) =>
        c.evaluation && c.evaluation.noteFinale !== c.evaluation.noteCalculee
          ? (c.evaluation.justificationEcart ?? 'Oui')
          : null,
    },
    {
      cle: 'eliminatoire',
      libelle: 'Critère éliminatoire',
      format: 'long',
      valeur: (c) => (c.evaluation ? (c.evaluation.eliminatoire ? (c.evaluation.motifEliminatoire ?? 'Oui') : 'Non') : null),
    },
    {
      cle: 'profil_modifie',
      libelle: 'Profil modifié depuis l’entretien',
      format: 'texte',
      valeur: (c) =>
        c.evaluation && c.profilModifieLe && c.profilModifieLe > c.evaluation.evalueeLe ? dateTunis(c.profilModifieLe) : null,
    },
    { cle: 'entretiens', libelle: 'Entretiens notés', format: 'nombre', valeur: (c) => c.nbEvaluations || null },
  ]);

  const axes = enGroupe(
    'Notes par axe',
    AXES.map((axe) => ({
      cle: `axe.${axe}`,
      libelle: LIBELLES_AXES[axe],
      format: 'nombre',
      sens: 'haut',
      valeur: (c) => c.evaluation?.scores[axe] ?? null,
    })),
  );

  const poids = enGroupe(GROUPE_POIDS, [
    { cle: 'bareme', libelle: 'Version du barème', format: 'nombre', valeur: (c) => c.evaluation?.baremeVersion ?? null },
    ...AXES.map(
      (axe): SansGroupe => ({
        cle: `poids.${axe}`,
        libelle: LIBELLES_AXES[axe],
        format: 'nombre',
        valeur: (c) => c.evaluation?.poids[axe] ?? null,
      }),
    ),
  ]);

  const dossier = enGroupe('Dossier', [
    { cle: 'charte', libelle: 'Charte signée le', format: 'texte', valeur: (c) => dateTunis(c.charteSigneeLe) },
    { cle: 'rapport', libelle: 'Rapport de référence signé le', format: 'texte', valeur: (c) => dateTunis(c.rapportSigneLe) },
    {
      cle: 'justificatifs',
      libelle: 'Justificatifs obligatoires reçus',
      format: 'texte',
      valeur: (c) => (c.justificatifs.attendus > 0 ? `${c.justificatifs.recus} sur ${c.justificatifs.attendus}` : null),
    },
    {
      cle: 'justificatifs_retard',
      libelle: 'Justificatifs en retard',
      format: 'nombre',
      sens: 'bas',
      valeur: (c) => (c.justificatifs.attendus > 0 ? c.justificatifs.enRetard : null),
    },
    {
      cle: 'justificatifs_echeance',
      libelle: 'Échéance des justificatifs',
      format: 'texte',
      valeur: (c) => dateTunis(c.justificatifs.echeance),
    },
  ]);

  return [...candidature, ...tri, ...telephone, ...entretien, ...axes, ...poids, ...dossier];
}

/**
 * Candidats en tête d'une ligne classable. Rien n'est signalé si moins de deux
 * candidats ont une valeur, ou s'ils sont tous à égalité.
 */
export function meilleursDeLigne(ligne: LigneComparaison, candidats: readonly CandidatCompare[]): boolean[] {
  const valeurs = candidats.map((c) => ligne.valeur(c));
  const nombres = valeurs.filter((v): v is number => typeof v === 'number');
  if (!ligne.sens || nombres.length < 2) return valeurs.map(() => false);
  const cible = ligne.sens === 'haut' ? Math.max(...nombres) : Math.min(...nombres);
  if (nombres.every((n) => n === cible)) return valeurs.map(() => false);
  return valeurs.map((v) => v === cible);
}
